import { useMemo, useState } from 'react';

import { Modal } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { SearchInput, Checkbox } from '../ui/Field';
import { Badge, Pill } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { EmptyState, LoadingState } from '../ui/AsyncBoundary';

/**
 * Choose what to show.
 *
 * The list is not hard-coded — it is whatever the ingest pipeline has actually
 * observed arriving from this organisation's devices. Two customers running
 * different TCU hardware see different lists, and a device that starts
 * reporting a new field shows up here within a packet or two without anyone
 * changing configuration.
 *
 * Discovered attributes are separated from the standard ones and labelled with
 * the hardware they came from, because "which of my devices even send this?"
 * is the first question anyone asks when they see an unfamiliar field.
 */
export function AttributePicker({
  open,
  onClose,
  attributes,
  loading,
  selected,
  onChange,
  maxColumns = 8,
  title = 'Choose columns',
  description = 'Pick the readings to show for every vehicle. The list is built from what your devices actually send.',
}) {
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(selected);

  // Re-seed the draft each time the dialog opens, so cancelling really cancels.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(selected);
      setQuery('');
    }
  }

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const groups = new Map();

    for (const attribute of attributes || []) {
      if (
        needle &&
        !attribute.label.toLowerCase().includes(needle) &&
        !attribute.key.toLowerCase().includes(needle)
      ) {
        continue;
      }
      const group = attribute.group || 'Other';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(attribute);
    }

    return [...groups.entries()];
  }, [attributes, query]);

  const toggle = (key) => {
    setDraft((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  };

  const atLimit = draft.length >= maxColumns;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={title}
      description={description}
      footer={
        <>
          <span className="mr-auto text-[12.5px] text-ink-3 tabular-nums">
            {draft.length} of {maxColumns} chosen
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => {
              onChange(draft);
              onClose();
            }}
          >
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search attributes"
            className="flex-1"
          />
          {draft.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setDraft([])}>
              Clear all
            </Button>
          )}
        </div>

        {loading ? (
          <LoadingState label="Reading what your devices send" />
        ) : !grouped.length ? (
          <EmptyState
            icon="search"
            title="No attributes match"
            description="Try a different search, or check that your devices have reported recently."
          />
        ) : (
          <div className="max-h-[52vh] space-y-4 overflow-y-auto pr-1">
            {grouped.map(([group, items]) => (
              <section key={group}>
                <div className="mb-1.5 flex items-center gap-2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
                    {group}
                  </h4>
                  {group === 'Reported by device' && (
                    <Badge tone="accent" size="sm" icon={null}>
                      discovered
                    </Badge>
                  )}
                </div>

                <ul className="grid gap-1 sm:grid-cols-2">
                  {items.map((attribute) => {
                    const isSelected = draft.includes(attribute.key);
                    const disabled = !isSelected && atLimit;

                    return (
                      <li key={attribute.key}>
                        <label
                          className={[
                            'flex cursor-pointer items-start gap-2.5 rounded-lg border px-2.5 py-2 transition-colors',
                            isSelected
                              ? 'border-brand-border bg-brand-soft'
                              : disabled
                                ? 'border-line opacity-45'
                                : 'border-line hover:bg-surface-2',
                          ].join(' ')}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[color:var(--brand)]"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={() => toggle(attribute.key)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline gap-1.5">
                              <span className="truncate text-[13.5px] text-ink">{attribute.label}</span>
                              {attribute.unit && <Pill>{attribute.unit}</Pill>}
                            </span>
                            <span className="mt-0.5 block truncate text-[11.5px] text-ink-3">
                              {attribute.sample !== null && attribute.sample !== undefined
                                ? `e.g. ${attribute.sample}`
                                : attribute.key}
                              {attribute.models?.length
                                ? ` · ${attribute.models.join(', ')}`
                                : ''}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}

        {atLimit && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-ink-3">
            <Icon name="info" size="xs" />
            {maxColumns} columns is the limit — the grid stops being readable past that. Remove one
            to add another.
          </p>
        )}
      </div>
    </Modal>
  );
}
